/**
 * Centralized logging service that captures logs for debugging and bug reports
 * Maintains a circular buffer of recent logs that can be included in bug reports
 */

import { AppUI } from '@seedlang/state';

class Logger {
  constructor() {
    this.logBuffer = [];
    this.maxBufferSize = 400; // Shared buffer limit across environments
    const devFlag = typeof __DEV__ !== 'undefined'
      ? __DEV__
      : (typeof process !== 'undefined' ? process.env.NODE_ENV !== 'production' : true);
    this.isProduction = !devFlag;
    this.enableProductionLogging = true; // Enable logging in production for bug reports
    this.logLevels = {
      DEBUG: 0,
      INFO: 1,
      WARN: 2,
      ERROR: 3
    };
    this.currentLogLevel = this.isProduction ? this.logLevels.WARN : this.logLevels.DEBUG;

    // Always setup console interception, but check enabled state dynamically
    this.setupConsoleInterception();
  }

  // Format timestamp as MM-DD-HH:mm:ss
  formatTimestamp(date = new Date()) {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${month}-${day}-${hours}:${minutes}:${seconds}`;
  }

  // Dynamic check for logging enabled state
  isLoggingEnabled() {
    // ENABLE_MOBILE_LOGGING is defined as false in AppUI class
    // Since it's an instance property, accessing it on the class returns undefined
    // We explicitly check for true to ensure logging is disabled by default
    return AppUI.ENABLE_MOBILE_LOGGING === true;
  }

  setupConsoleInterception() {
    // Store original console methods
    this.originalConsole = {
      log: console.log,
      warn: console.warn,
      error: console.error,
      debug: console.debug
    };

    // Don't override console methods anymore - let them pass through normally
    // Only scoped loggers will add to the buffer
  }

  shouldLimitBuffer() {
    return true;
  }

  trimBufferIfNeeded(useSlice = false) {
    if (!this.shouldLimitBuffer()) {
      return;
    }

    if (this.logBuffer.length <= this.maxBufferSize) {
      return;
    }

    if (useSlice) {
      this.logBuffer = this.logBuffer.slice(-this.maxBufferSize);
    } else {
      this.logBuffer.shift();
    }
  }

  log(level, args) {
    // Skip logging if globally disabled (check dynamically)
    if (!this.isLoggingEnabled()) {
      return;
    }

    // Skip logging in production unless explicitly enabled
    if (this.isProduction && !this.enableProductionLogging) {
      return;
    }

    const logEntry = {
      timestamp: this.formatTimestamp(),
      level,
      message: this.formatArgs(args),
      context: this.getContext()
    };

    // Add to circular buffer
    this.logBuffer.push(logEntry);
    this.trimBufferIfNeeded();
  }

  formatArgs(args) {
    return args.map(arg => {
      if (typeof arg === 'object') {
        try {
          return JSON.stringify(arg, null, 2);
        } catch (e) {
          return '[Circular Object]';
        }
      }
      // Ensure proper string conversion and handle special characters
      let str = String(arg);
      // Remove any null bytes or invalid UTF-8 sequences
      str = str.replace(/\0/g, '');
      // Replace any non-printable characters (except newlines and tabs)
      str = str.replace(/[^\x20-\x7E\n\t]/g, (char) => {
        // Keep common Unicode characters, replace others with their code
        const code = char.charCodeAt(0);
        if (code >= 0x80 && code <= 0xFFFF) {
          return char; // Keep valid Unicode
        }
        return `\\u${code.toString(16).padStart(4, '0')}`;
      });
      return str;
    }).join(' ');
  }

  getContext() {
    // Add any relevant context (could expand this)
    return {
      timestamp: Date.now()
    };
  }

  // Method to get logs for bug reports
  getLogsForBugReport(options = {}) {
    const includeDebug = options.includeDebug !== undefined ? options.includeDebug : false;
    const format = options.format !== undefined ? options.format : 'text';
    const defaultMaxLines = this.shouldLimitBuffer() ? 200 : null;
    const maxLines = options.maxLines !== undefined ? options.maxLines : defaultMaxLines;

    // Return empty or a message if logging is disabled globally AND no logs exist
    // (Allow bug reports even if logging is disabled, in case scoped loggers added entries)
    if (!this.isLoggingEnabled() && this.logBuffer.length === 0) {
      return format === 'json' ? '[]' : 'Logging is disabled and no logs available';
    }

    // In production, always allow bug report logs if buffer has content
    // Scoped loggers add to buffer regardless of enableProductionLogging flag

    let logs = this.logBuffer;

    // Filter out debug logs in production unless specifically requested
    if (this.isProduction && !includeDebug) {
      logs = logs.filter(log => log.level !== 'DEBUG');
    }

    // Get the most recent logs up to maxLines (if limit applies)
    if (Number.isFinite(maxLines)) {
      logs = logs.slice(-maxLines);
    }

    if (format === 'json') {
      return JSON.stringify(logs, null, 2);
    }

    // Format as text
    const logText = logs.map(log => {
      // Ensure all parts are properly encoded
      const timestamp = String(log.timestamp).replace(/[^\x20-\x7E]/g, '');
      const level = String(log.level).replace(/[^\x20-\x7E]/g, '');
      // Message should already be sanitized by formatArgs, but double-check
      const message = String(log.message).replace(/\0/g, '');
      // Only show log level for WARN and ERROR
      if (level === 'WARN' || level === 'ERROR') {
        return `[${timestamp}] [${level}] ${message}`;
      } else {
        return `[${timestamp}] ${message}`;
      }
    }).join('\n');

    // Final safety check - ensure the entire string is valid UTF-8
    return logText.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
  }

  // Clear logs (useful after sending bug report)
  clearLogs() {
    this.logBuffer = [];
  }

  // Set max buffer size (useful for memory management)
  setMaxBufferSize(size) {
    this.maxBufferSize = Math.max(50, Math.min(1000, size)); // Clamp between 50-1000
    // Trim existing buffer if needed
    this.trimBufferIfNeeded(true);
  }

  // Get current memory usage estimate
  getMemoryUsage() {
    const avgEntrySize = 200; // bytes
    const currentUsage = this.logBuffer.length * avgEntrySize;
    const currentText = `${(currentUsage / 1024).toFixed(1)}KB`;

    if (!this.shouldLimitBuffer()) {
      return {
        current: currentText,
        maximum: 'Unlimited',
        entries: this.logBuffer.length
      };
    }

    const maxUsage = this.maxBufferSize * avgEntrySize;
    return {
      current: currentText,
      maximum: `${(maxUsage / 1024).toFixed(1)}KB`,
      entries: this.logBuffer.length
    };
  }

  // Enable or disable logging in production
  setProductionLogging(enabled) {
    this.enableProductionLogging = enabled;
    if (enabled && this.isProduction) {
      console.log('[Logger] Production logging enabled');
    }
  }

  // Test method to verify logging is working in production
  testProductionLogging() {
    const testEntry = {
      timestamp: this.formatTimestamp(),
      level: 'INFO',
      message: '[Logger] Test entry - logging system is working',
      context: { test: true }
    };

    this.logBuffer.push(testEntry);
    this.trimBufferIfNeeded();

    const status = {
      isProduction: this.isProduction,
      enableProductionLogging: this.enableProductionLogging,
      globalLoggingEnabled: this.isLoggingEnabled(),
      bufferSize: this.logBuffer.length,
      canGetBugReport: this.getLogsForBugReport({ maxLines: 1 }).length > 0
    };

    console.log('[Logger] Test status:', status);
    return status;
  }

  // Create a scoped logger for specific components
  createScoped(scope) {
    const self = this;
    return {
      log: (...args) => {
        // Always add to buffer for debugging, regardless of logging enabled state
        const message = `[${scope}] ${args.map(arg => {
          if (typeof arg === 'object') {
            try {
              return JSON.stringify(arg);
            } catch (e) {
              return '[Object]';
            }
          }
          return String(arg);
        }).join(' ')}`;

        self.logBuffer.push({
          timestamp: self.formatTimestamp(),
          level: 'INFO',
          message: message,
          context: { scope }
        });

        self.trimBufferIfNeeded();

        // Also output to console in development if logging is enabled
        if (!self.isProduction && self.isLoggingEnabled()) {
          self.originalConsole.log(`[${scope}]`, ...args);
        }
      },
      warn: (...args) => {
        const message = `[${scope}] ${args.map(arg => {
          if (typeof arg === 'object') {
            try {
              return JSON.stringify(arg);
            } catch (e) {
              return '[Object]';
            }
          }
          return String(arg);
        }).join(' ')}`;

        self.logBuffer.push({
          timestamp: self.formatTimestamp(),
          level: 'WARN',
          message: message,
          context: { scope }
        });

        self.trimBufferIfNeeded();

        if (!self.isProduction && self.isLoggingEnabled()) {
          self.originalConsole.warn(`[${scope}]`, ...args);
        }
      },
      error: (...args) => {
        const message = `[${scope}] ${args.map(arg => {
          if (typeof arg === 'object') {
            try {
              return JSON.stringify(arg);
            } catch (e) {
              return '[Object]';
            }
          }
          return String(arg);
        }).join(' ')}`;

        self.logBuffer.push({
          timestamp: self.formatTimestamp(),
          level: 'ERROR',
          message: message,
          context: { scope }
        });

        self.trimBufferIfNeeded();

        if (!self.isProduction && self.isLoggingEnabled()) {
          self.originalConsole.error(`[${scope}]`, ...args);
        }
      },
      debug: (...args) => {
        const message = `[${scope}] ${args.map(arg => {
          if (typeof arg === 'object') {
            try {
              return JSON.stringify(arg);
            } catch (e) {
              return '[Object]';
            }
          }
          return String(arg);
        }).join(' ')}`;

        self.logBuffer.push({
          timestamp: self.formatTimestamp(),
          level: 'DEBUG',
          message: message,
          context: { scope }
        });

        self.trimBufferIfNeeded();

        if (!self.isProduction && self.isLoggingEnabled()) {
          self.originalConsole.debug(`[${scope}]`, ...args);
        }
      }
    };
  }
}

// Create singleton instance
const logger = new Logger();

export default logger;
